'use client';

import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import Link from 'next/link';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getStripe } from '@/lib/stripe/client';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AdPayment {
  id: string;
  billing_name: string;
  billing_email: string;
  company_name: string | null;
  plan: string;
  ad_type: string;
  placement: string | null;
  amount: number;
  currency: string;
  payment_status: string;
  payment_intent_id: string | null;
  campaign_start: string | null;
  campaign_end: string | null;
  created_at: string;
}

interface AdPlan {
  id: string;
  name: string;
  price: number;
  adType: string;
  placement: string;
  duration: string;
  description: string;
  features: string[];
  badge?: string;
}

// ─── Ad Plans ────────────────────────────────────────────────────────────────

const AD_PLANS: AdPlan[] = [
  {
    id: 'banner-ad-weekly',
    name: 'Banner Ad — Weekly',
    price: 25,
    adType: 'banner',
    placement: 'banner',
    duration: '7 days',
    description: 'Display banner ad across ViddStarTV for 7 days.',
    features: ['Homepage banner placement', 'Runs for 7 days', 'Appears on all pages', 'Link to your site'],
  },
  {
    id: 'banner-ad-monthly',
    name: 'Banner Ad — Monthly',
    price: 75,
    adType: 'banner',
    placement: 'banner',
    duration: '30 days',
    description: 'Display banner ad across ViddStarTV for 30 days.',
    badge: 'Best Value',
    features: ['Homepage banner placement', 'Runs for 30 days', 'Appears on all pages', 'Priority placement'],
  },
  {
    id: 'video-ad-weekly',
    name: 'Video Ad — Weekly',
    price: 50,
    adType: 'video',
    placement: 'pre-roll',
    duration: '7 days',
    description: 'Pre-roll video ad shown before videos for 7 days.',
    features: ['Pre-roll placement', 'Runs for 7 days', 'Skippable after 5s', 'Full analytics'],
  },
  {
    id: 'video-ad-monthly',
    name: 'Video Ad — Monthly',
    price: 150,
    adType: 'video',
    placement: 'pre-roll',
    duration: '30 days',
    description: 'Pre-roll video ad shown before videos for 30 days.',
    badge: 'Popular',
    features: ['Pre-roll placement', 'Runs for 30 days', 'Skippable after 5s', 'Priority rotation'],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(status: string): string {
  switch (status) {
    case 'succeeded': return 'text-green-400 bg-green-400/10 border-green-400/30';
    case 'pending': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    default: return 'text-text-muted bg-surface-tertiary border-stroke-primary';
  }
}

// ─── Payment Form ─────────────────────────────────────────────────────────────

interface PaymentFormProps {
  plan: AdPlan;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentFormInner({ plan, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState('');
  const supabase = createClient();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setPayError('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/advertiser` },
      redirect: 'if_required',
    });

    if (error) {
      setPayError(error.message || 'Payment failed. Please try again.');
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      await supabase.functions.invoke('confirm-payment', {
        body: { paymentIntentId: paymentIntent.id, tableName: 'advertiser_payments' },
      });
      onSuccess();
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-text-primary font-bold text-sm">{plan.name}</span>
          <span className="text-brand-red font-bold text-xl">${plan.price}</span>
        </div>
        <p className="text-text-muted text-xs">{plan.description}</p>
      </div>

      <PaymentElement />

      {payError && (
        <p className="text-red-400 text-sm border border-red-400/30 bg-red-400/10 rounded-[4px] px-3 py-2">{payError}</p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 border border-stroke-primary text-text-secondary hover:text-text-primary text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors disabled:opacity-50"
        >
          {processing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Processing...
            </span>
          ) : (
            `Pay $${plan.price}`
          )}
        </button>
      </div>
    </form>
  );
}

// ─── Checkout Step ────────────────────────────────────────────────────────────

interface CheckoutStepProps {
  plan: AdPlan;
  onSuccess: () => void;
  onBack: () => void;
}

function CheckoutStep({ plan, onSuccess, onBack }: CheckoutStepProps) {
  const { user, profile } = useAuth();
  const supabase = createClient();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [firstName, setFirstName] = useState(() => {
    if ((profile as any)?.full_name) return (profile as any).full_name.split(' ')[0] || '';
    return '';
  });
  const [lastName, setLastName] = useState(() => {
    if ((profile as any)?.full_name) {
      const parts = (profile as any).full_name.split(' ');
      return parts.slice(1).join(' ') || '';
    }
    return '';
  });
  const [email, setEmail] = useState(user?.email || '');
  const [company, setCompany] = useState('');

  const handleInitPayment = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const now = new Date();
    const durationDays = plan.duration.includes('30') ? 30 : 7;
    const campaignEnd = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);

    try {
      const { data, error: fnError } = await supabase.functions.invoke<{
        clientSecret: string;
        recordId: string;
      }>('create-payment-intent', {
        body: {
          paymentData: {
            amount: plan.price,
            currency: 'usd',
            tableName: 'advertiser_payments',
            description: `ViddStar Ad: ${plan.name}`,
            additionalFields: {
              plan: plan.id,
              ad_type: plan.adType,
              placement: plan.placement,
              company_name: company.trim() || null,
              campaign_start: now.toISOString(),
              campaign_end: campaignEnd.toISOString(),
            },
          },
          customerInfo: {
            userId: user?.id ?? null,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            stripeCustomerId: (profile as any)?.stripe_customer_id ?? null,
          },
        },
      });

      if (fnError) {
        throw new Error((data as any)?.error ?? fnError.message ?? 'Payment setup failed');
      }
      if (!data?.clientSecret) throw new Error('No client secret returned');
      setClientSecret(data.clientSecret);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stripePromise = getStripe();

  if (clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
        <PaymentFormInner plan={plan} onSuccess={onSuccess} onCancel={() => setClientSecret(null)} />
      </Elements>
    );
  }

  return (
    <form onSubmit={handleInitPayment} className="space-y-4">
      <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-text-primary font-bold text-sm">{plan.name}</span>
          <span className="text-brand-red font-bold text-xl">${plan.price}</span>
        </div>
        <p className="text-text-muted text-xs">{plan.description}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {plan.features.map((f, i) => (
            <span key={i} className="text-xs text-text-secondary bg-surface-primary border border-stroke-primary px-2 py-0.5 rounded-[4px]">
              {f}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">First Name *</label>
          <input
            type="text" required value={firstName} onChange={e => setFirstName(e.target.value)}
            className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
            placeholder="John"
          />
        </div>
        <div>
          <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">Last Name *</label>
          <input
            type="text" required value={lastName} onChange={e => setLastName(e.target.value)}
            className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
            placeholder="Doe"
          />
        </div>
      </div>

      <div>
        <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">Email Address *</label>
        <input
          type="email" required value={email} onChange={e => setEmail(e.target.value)}
          className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
          placeholder="you@company.com"
        />
      </div>

      <div>
        <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">Company Name</label>
        <input
          type="text" value={company} onChange={e => setCompany(e.target.value)}
          className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
          placeholder="Your Company (optional)"
        />
      </div>

      {error && (
        <p className="text-red-400 text-sm border border-red-400/30 bg-red-400/10 rounded-[4px] px-3 py-2">{error}</p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button" onClick={onBack}
          className="flex-1 border border-stroke-primary text-text-secondary hover:text-text-primary text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors"
        >
          Back
        </button>
        <button
          type="submit" disabled={loading}
          className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Setting up...
            </span>
          ) : 'Continue to Payment'}
        </button>
      </div>
    </form>
  );
}

// ─── Buy Ad Modal ─────────────────────────────────────────────────────────────

interface BuyAdModalProps {
  onClose: () => void;
  onPurchased: () => void;
}

function BuyAdModal({ onClose, onPurchased }: BuyAdModalProps) {
  const [step, setStep] = useState<'browse' | 'checkout' | 'success'>('browse');
  const [selectedPlan, setSelectedPlan] = useState<AdPlan | null>(null);

  const handleSuccess = () => {
    setStep('success');
    onPurchased();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-surface-primary border border-stroke-primary rounded-[4px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary flex-shrink-0">
          <div>
            {step === 'browse' && <h2 className="text-text-primary font-bold text-base uppercase tracking-wide">Purchase Ad Placement</h2>}
            {step === 'checkout' && <h2 className="text-text-primary font-bold text-base uppercase tracking-wide">Checkout</h2>}
            {step === 'success' && <h2 className="text-green-400 font-bold text-base uppercase tracking-wide">Payment Successful</h2>}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === 'browse' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {AD_PLANS.map(plan => (
                <div key={plan.id} className="border border-stroke-primary bg-surface-secondary hover:border-brand-red transition-colors rounded-[4px] p-4 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      {plan.badge && (
                        <span className="inline-block text-white text-xs font-bold uppercase px-2 py-0.5 rounded-[2px] mb-1 bg-brand-red">{plan.badge}</span>
                      )}
                      <h3 className="text-text-primary font-bold text-sm leading-snug">{plan.name}</h3>
                    </div>
                    <span className="text-brand-red font-bold text-xl flex-shrink-0">${plan.price}</span>
                  </div>
                  <p className="text-text-muted text-xs mb-3">{plan.description}</p>
                  <ul className="space-y-1 mb-4 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                        <svg className="w-3.5 h-3.5 text-brand-red flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => { setSelectedPlan(plan); setStep('checkout'); }}
                    className="w-full bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase py-2 rounded-[4px] transition-colors"
                  >
                    Get Started — ${plan.price}
                  </button>
                </div>
              ))}
            </div>
          )}

          {step === 'checkout' && selectedPlan && (
            <CheckoutStep plan={selectedPlan} onSuccess={handleSuccess} onBack={() => setStep('browse')} />
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 bg-green-500/20 border border-green-500/40 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-text-primary font-bold text-xl mb-2">Payment Successful!</h3>
              <p className="text-text-secondary text-sm mb-1">
                Your ad campaign for <strong className="text-text-primary">{selectedPlan?.name}</strong> is now active.
              </p>
              <p className="text-text-muted text-xs mb-6">Our team will activate your placement shortly. Check your email for confirmation.</p>
              <button onClick={onClose} className="bg-brand-red hover:bg-brand-red-hover text-white font-bold uppercase px-8 py-2.5 rounded-[4px] transition-colors">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdRevenuePage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [payments, setPayments] = useState<AdPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const fetchPayments = useCallback(async (billingEmail: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('advertiser_payments')
        .select('*')
        .eq('billing_email', billingEmail.toLowerCase())
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPayments((data || []) as AdPayment[]);
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      fetchPayments(user.email);
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleLookup = async () => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setLookupError('Please enter a valid email address.');
      return;
    }
    setLookupError('');
    setLookupLoading(true);
    setEmail(trimmed);
    await fetchPayments(trimmed);
    setLookupLoading(false);
  };

  const handlePurchased = () => {
    showToast('Ad campaign purchased! Refreshing spend history...');
    if (email) {
      setTimeout(() => fetchPayments(email), 1500);
    }
  };

  // Summary stats
  const totalSpend = payments.reduce((sum, p) => sum + (p.payment_status === 'succeeded' ? p.amount : 0), 0);
  const activeCampaigns = payments.filter(p => p.payment_status === 'succeeded').length;
  const pendingPayments = payments.filter(p => p.payment_status === 'pending').length;

  const PLAN_LABELS: Record<string, string> = {
    'banner-ad-weekly': 'Banner Ad — Weekly',
    'banner-ad-monthly': 'Banner Ad — Monthly',
    'video-ad-weekly': 'Video Ad — Weekly',
    'video-ad-monthly': 'Video Ad — Monthly',
  };

  return (
    <div className="min-h-screen bg-surface-primary text-text-primary">
      {/* Header */}
      <header className="border-b border-stroke-primary bg-surface-secondary sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link href="/" className="flex items-center gap-2 text-brand-red hover:opacity-80 transition-opacity">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-xs font-bold uppercase tracking-widest hidden sm:inline">Back</span>
            </Link>
            <div className="w-px h-5 bg-stroke-primary" />
            <h1 className="text-text-primary font-bold text-base uppercase tracking-wide">Ad Revenue</h1>
          </div>
          <button
            onClick={() => setShowBuyModal(true)}
            className="flex items-center gap-1.5 bg-brand-red hover:bg-brand-red-hover text-white text-xs font-bold uppercase px-3 py-2 rounded-[4px] transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Buy Ad Placement
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Email lookup for guests */}
        {!user && (
          <div className="mb-8 bg-surface-secondary border border-stroke-primary rounded-[4px] p-5">
            <p className="text-text-secondary text-sm mb-3">
              Enter the email used when purchasing your ad placement to view your spend history.
            </p>
            <div className="flex gap-2">
              <input
                type="email" value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                placeholder="billing@yourcompany.com"
                className="flex-1 bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2.5 rounded-[4px] focus:outline-none focus:border-brand-red transition-colors"
              />
              <button
                onClick={handleLookup} disabled={lookupLoading}
                className="bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors disabled:opacity-50"
              >
                {lookupLoading ? 'Looking up…' : 'Look Up'}
              </button>
            </div>
            {lookupError && <p className="mt-2 text-red-400 text-xs">{lookupError}</p>}
          </div>
        )}

        {/* Logged-in greeting */}
        {user && (
          <div className="mb-6">
            <p className="text-text-muted text-sm">
              Ad spend for <span className="text-brand-red font-bold">{user.email}</span>
            </p>
          </div>
        )}

        {/* Summary stats */}
        {(email || user) && !loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
            {[
              { label: 'Total Ad Spend', value: formatCents(totalSpend), accent: true },
              { label: 'Paid Campaigns', value: activeCampaigns.toString(), accent: false },
              { label: 'Pending', value: pendingPayments.toString(), accent: false },
            ].map(stat => (
              <div key={stat.label} className="bg-surface-card border border-stroke-primary rounded-[4px] px-4 py-3">
                <p className="text-text-muted text-xs uppercase tracking-widest mb-1">{stat.label}</p>
                <p className={`font-bold text-xl ${stat.accent ? 'text-brand-red' : 'text-text-primary'}`}>{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-surface-card border border-stroke-primary rounded-[4px] animate-pulse" />
            ))}
          </div>
        )}

        {/* No email entered */}
        {!loading && !email && !user && (
          <div className="text-center py-16">
            <svg className="w-12 h-12 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-text-muted text-sm">Enter your billing email above to view your ad spend history.</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && email && payments.length === 0 && (
          <div className="text-center py-16 border border-stroke-primary rounded-[4px] bg-surface-card">
            <svg className="w-12 h-12 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a13.937 13.937 0 01-3.417.894l-2.147-6.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-text-primary font-bold mb-1">No ad payments found</p>
            <p className="text-text-muted text-sm mb-5">No ad purchases found for <span className="text-brand-red">{email}</span>.</p>
            <button
              onClick={() => setShowBuyModal(true)}
              className="bg-brand-red hover:bg-brand-red-hover text-white text-sm font-bold uppercase px-5 py-2.5 rounded-[4px] transition-colors"
            >
              Purchase Ad Placement
            </button>
          </div>
        )}

        {/* Payment history table */}
        {!loading && payments.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-text-secondary text-xs uppercase tracking-widest">
                {payments.length} Payment{payments.length !== 1 ? 's' : ''}
              </h2>
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block border border-stroke-primary rounded-[4px] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-secondary border-b border-stroke-primary">
                    <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Plan</th>
                    <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Type</th>
                    <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Amount</th>
                    <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Status</th>
                    <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Date</th>
                    <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={p.id} className={`border-b border-stroke-primary last:border-0 ${i % 2 === 0 ? 'bg-surface-card' : 'bg-surface-secondary'}`}>
                      <td className="px-4 py-3 text-text-primary font-medium">{PLAN_LABELS[p.plan] || p.plan}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-text-secondary capitalize">{p.ad_type}</span>
                      </td>
                      <td className="px-4 py-3 text-brand-red font-bold">{formatCents(p.amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-[4px] border ${statusColor(p.payment_status)}`}>
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-muted text-xs">{formatDate(p.created_at)}</td>
                      <td className="px-4 py-3 text-text-muted text-xs">
                        {p.campaign_start && p.campaign_end
                          ? `${formatDate(p.campaign_start)} – ${formatDate(p.campaign_end)}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {payments.map(p => (
                <div key={p.id} className="border border-stroke-primary rounded-[4px] bg-surface-card p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-text-primary font-bold text-sm">{PLAN_LABELS[p.plan] || p.plan}</p>
                      <p className="text-text-muted text-xs mt-0.5">{formatDate(p.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-brand-red font-bold">{formatCents(p.amount)}</p>
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-[4px] border ${statusColor(p.payment_status)}`}>
                        {p.payment_status}
                      </span>
                    </div>
                  </div>
                  {p.campaign_start && p.campaign_end && (
                    <p className="text-text-muted text-xs border-t border-stroke-primary pt-2 mt-2">
                      Campaign: {formatDate(p.campaign_start)} – {formatDate(p.campaign_end)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA section */}
        <div className="mt-12 border-t border-stroke-primary pt-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-surface-card border border-stroke-primary rounded-[4px] p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-brand-red/10 border border-brand-red/30 rounded-[4px] flex items-center justify-center">
                  <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a13.937 13.937 0 01-3.417.894l-2.147-6.15M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-text-primary font-bold text-sm">Run a New Ad</p>
              </div>
              <p className="text-text-muted text-xs mb-3">Banner ads from $25/week. Video ads from $50/week.</p>
              <button
                onClick={() => setShowBuyModal(true)}
                className="text-brand-red text-xs font-bold uppercase hover:underline"
              >
                View Ad Plans →
              </button>
            </div>
            <div className="bg-surface-card border border-stroke-primary rounded-[4px] p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-surface-tertiary border border-stroke-primary rounded-[4px] flex items-center justify-center">
                  <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="text-text-primary font-bold text-sm">Full Campaign Dashboard</p>
              </div>
              <p className="text-text-muted text-xs mb-3">View impressions, clicks, and CTR for your active campaigns.</p>
              <Link href="/advertiser" className="text-brand-red text-xs font-bold uppercase hover:underline">
                Go to Dashboard →
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Buy Ad Modal */}
      {showBuyModal && (
        <BuyAdModal
          onClose={() => setShowBuyModal(false)}
          onPurchased={handlePurchased}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-surface-overlay border border-stroke-light text-text-primary text-sm font-bold px-5 py-3 rounded-[4px] shadow-xl z-50 animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
